import { ReactNode, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Link,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import { Check, Close, FiberManualRecord, Search } from '@mui/icons-material';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
    lookalikeErrorCode,
    lookalikeErrorMessage,
    lookalikeJobErrorCode,
    LookalikeErrorCode,
    lookalikeReferencePatch,
    stampedComparison,
    rangeStatus,
} from '../steckbrief-lookalike';

import {
    CalculateSteckbriefRequest,
    CandidateMerkmalDto,
    LookalikeCandidateDto,
    LookalikeComparisonDto,
    LookalikePrognoseMetricKey,
    MerkmalKey,
    MerkmalVerdict,
    RangeDto,
    RangeStatus,
} from '../ensan-steckbrief.dto';
import { ensanSteckbriefService } from '../ensan-steckbrief.service';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const POLL_INTERVAL_MS = 4_000;

/** A lookalike's card-joined total is shown at the derived total's precision (nearest 10,000 €). */
const DERIVED_TOTAL_ROUNDING_EUR = 10_000;

/** How a Merkmal value renders: a year (no digit grouping), a number with unit, or plain text. */
type MerkmalFormat = { kind: 'year' } | { kind: 'number'; unit?: string } | { kind: 'text' };

/** The 12 Merkmale in table order, with their row label (i18next key). */
const MERKMALE: { key: MerkmalKey; labelKey: string; format: MerkmalFormat }[] = [
    { key: 'BUILDING_USE', labelKey: 'Usage', format: { kind: 'text' } },
    { key: 'YEAR_BUILT', labelKey: 'Construction year', format: { kind: 'year' } },
    { key: 'NET_FLOOR_AREA_M2', labelKey: 'Net floor area (NGF)', format: { kind: 'number', unit: 'm²' } },
    { key: 'LISTED_BUILDING', labelKey: 'Monument protection', format: { kind: 'text' } },
    { key: 'ROOF', labelKey: 'Roof', format: { kind: 'text' } },
    { key: 'FACADE', labelKey: 'Facade', format: { kind: 'text' } },
    { key: 'BASEMENT_CEILING', labelKey: 'Basement ceiling', format: { kind: 'text' } },
    { key: 'WINDOWS_RENEWED_YEAR', labelKey: 'Windows renewed (year)', format: { kind: 'year' } },
    { key: 'GLAZING', labelKey: 'Glazing', format: { kind: 'text' } },
    { key: 'LIGHTING', labelKey: 'Lighting', format: { kind: 'text' } },
    { key: 'HEATING_ENERGY_SOURCE', labelKey: 'Heating energy source', format: { kind: 'text' } },
    { key: 'HEAT_GENERATOR_YEAR', labelKey: 'Heat generator year', format: { kind: 'year' } },
];

/** The three Prognose-band metrics, with their label + unit. */
const PROGNOSE_METRICS: { key: LookalikePrognoseMetricKey; labelKey: string; unit: string; digits: number }[] = [
    { key: 'ENERGY_SAVING_PCT', labelKey: 'Energy saving potential', unit: '%', digits: 0 },
    { key: 'CO2_SAVING_T_A', labelKey: 'CO₂ saving', unit: 't/a', digits: 1 },
    { key: 'COST_SAVING_EUR_A', labelKey: 'Cost saving', unit: '€/a', digits: 0 },
];

/** ✓ / ● / ✕ traffic light of a gated verdict. */
const VERDICT_BADGE: Record<MerkmalVerdict, { labelKey: string; color: 'success' | 'warning' | 'error' }> = {
    TRIFFT_ZU: { labelKey: 'Matches', color: 'success' },
    ANNAEHERND: { labelKey: 'Approximate', color: 'warning' },
    ABWEICHUNG: { labelKey: 'Deviation', color: 'error' },
};

/** A Prognose flag reads on the same traffic light as a Merkmal verdict. */
const RANGE_STATUS_VERDICT: Record<RangeStatus, MerkmalVerdict> = {
    INSIDE: 'TRIFFT_ZU',
    NEAR: 'ANNAEHERND',
    OUTSIDE: 'ABWEICHUNG',
};

function isPresent(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
}

function formatMerkmalValue(t: TFunction, value: unknown, format: MerkmalFormat): string {
    if (!isPresent(value)) return '?';
    if (typeof value === 'boolean') return value ? t('Yes') : t('No');
    if (typeof value === 'number' && Number.isFinite(value)) {
        if (format.kind === 'year') return String(value);
        const number = value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
        return format.kind === 'number' && format.unit ? `${number} ${format.unit}` : number;
    }
    return String(value);
}

function formatNumber(value: number | null | undefined, unit: string, digits: number): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '–';
    return `${value.toLocaleString('de-DE', { maximumFractionDigits: digits })} ${unit}`;
}

function formatRange(range: RangeDto | null | undefined, unit: string, digits: number): string {
    if (!range) return '–';
    return `${formatNumber(range.low, unit, digits)} – ${formatNumber(range.high, unit, digits)}`;
}

function roundedTotal(value: number | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    return Math.round(value / DERIVED_TOTAL_ROUNDING_EUR) * DERIVED_TOTAL_ROUNDING_EUR;
}

/** Resolves after `ms` milliseconds, or rejects immediately if `signal` is already aborted. */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal.aborted) {
            reject(signal.reason);
            return;
        }
        const timer = setTimeout(resolve, ms);
        signal.addEventListener(
            'abort',
            () => {
                clearTimeout(timer);
                reject(signal.reason);
            },
            { once: true },
        );
    });
}

interface SteckbriefLookalikePanelProps {
    steckbriefId: string;
    /** The current `/calculate` inputs — searched verbatim (the subject's 12 Merkmale derive from them). */
    request: CalculateSteckbriefRequest;
    /** Header of the subject column ("Zu bewertendes Gebäude"): the Steckbrief name + a muted subline. */
    subjectName: string;
    subjectSubtitle?: string | null;
    /**
     * The effective Investitionskosten/m² (D3 — the advisor's bounds over the stamped lookalike
     * aggregation) and its derived total from the latest `/calculate`. The lookalikes' €/m² are
     * flagged against it; until `/calculate` caught up with a new search, the subject column shows
     * the search's own aggregation.
     */
    investmentCostPerSqmRange?: RangeDto | null;
    investmentCostDerivedTotalRange?: RangeDto | null;
    /**
     * Stamps a comparison as the Variant's lookalike reference context (D4 lineage) — called with
     * server-copied values when a search finishes and when the advisor picks another package.
     */
    onReferenceContext?: (patch: Record<string, unknown>) => void;
    /** `/calculate`'s `lookalikeReferenceStale`: the matching inputs changed since the stamped search. */
    referenceStale?: boolean | null;
    /**
     * The Variant's `lookalikeComparison` form value (JSON text): the comparison stamped by an earlier
     * search, shown at once on open — the search button runs a new one.
     */
    stampedComparisonValue?: unknown;
}

/**
 * The lookalike decision support (D4): a "search comparable buildings" button and the
 * Gebäudevergleich — the subject's 12 Merkmale beside the two lookalikes with rule-guided
 * traffic-light verdicts, the deterministic Prognose band (frozen system ranges vs the lookalikes'
 * actuals) and the Investitionskosten/m². Reviewing only — nothing is persisted; the advisor adjusts
 * the forecast in the Prognose editor (below) informed by what they see here.
 *
 * The search is async (POST → 202 jobId, then poll GET until READY/FAILED) to avoid Cloudflare's
 * 100 s origin timeout: the LLM search takes ~60 s; polling every 4 s keeps well within any limit.
 */
export function SteckbriefLookalikePanel({
    steckbriefId,
    request,
    subjectName,
    subjectSubtitle,
    investmentCostPerSqmRange,
    investmentCostDerivedTotalRange,
    onReferenceContext,
    referenceStale,
    stampedComparisonValue,
}: SteckbriefLookalikePanelProps) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<LookalikeComparisonDto | null>(() =>
        stampedComparison(stampedComparisonValue),
    );
    const [selecting, setSelecting] = useState(false);
    const [selectionFailed, setSelectionFailed] = useState(false);
    const [errorCode, setErrorCode] = useState<LookalikeErrorCode | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    function adopt(next: LookalikeComparisonDto) {
        setResult(next);
        onReferenceContext?.(lookalikeReferencePatch(next));
    }

    /** The Maßnahmenpaket picker: ids go out, the recomputed comparison comes back (server-side figures only). */
    async function selectPackage(docId: string, packageId: string) {
        if (!result) return;
        // The full selection (the endpoint is stateless); a lookalike compared without any package
        // is left out — the backend keeps it as is, and an empty id would be rejected.
        const packages = Object.fromEntries(
            result.candidates.flatMap((candidate) => {
                const picked = candidate.docId === docId ? packageId : candidate.packageId;
                return picked ? [[candidate.docId, picked]] : [];
            }),
        );
        setSelecting(true);
        setSelectionFailed(false);
        try {
            adopt(
                await ensanSteckbriefService.selectLookalikePackages(steckbriefId, { comparison: result, packages }),
            );
        } catch {
            setSelectionFailed(true);
        } finally {
            setSelecting(false);
        }
    }

    async function search() {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        setLoading(true);
        setErrorCode(null);
        try {
            const { jobId } = await ensanSteckbriefService.enqueueLookalikes(steckbriefId, request, {
                signal: controller.signal,
            });
            if (controller.signal.aborted) return;

            // eslint-disable-next-line no-constant-condition
            while (true) {
                await sleep(POLL_INTERVAL_MS, controller.signal);
                const job = await ensanSteckbriefService.getLookalikeJob(steckbriefId, jobId, {
                    signal: controller.signal,
                });
                if (controller.signal.aborted) return;

                if (job.status === 'READY') {
                    if (job.result) adopt(job.result);
                    else setResult(null);
                    return;
                }
                if (job.status === 'FAILED') {
                    setResult(null);
                    setErrorCode(lookalikeJobErrorCode(job.errorCode));
                    return;
                }
                // PENDING: keep polling
            }
        } catch (error) {
            if (controller.signal.aborted) return;
            setResult(null);
            setErrorCode(lookalikeErrorCode(error));
        } finally {
            if (abortRef.current === controller) setLoading(false);
        }
    }

    return (
        <Stack spacing={2} data-testid="lookalike-panel">
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ justifyContent: 'space-between', alignItems: { md: 'flex-start' } }}
            >
                <Box>
                    <Typography variant="subtitle2">{t('Building comparison')}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                        {t(
                            'Search earlier Energieberatungen for the two most comparable buildings. Reviewing changes nothing — use it to inform the forecast you adjust below. The search takes about a minute.',
                        )}
                    </Typography>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={loading ? <CircularProgress size={16} /> : <Search />}
                        disabled={loading}
                        onClick={search}
                        data-testid="lookalike-search"
                    >
                        {loading ? t('Searching comparable buildings…') : t('Search comparable buildings')}
                    </Button>
                </Box>
                {result?.status === 'OK' && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', maxWidth: { md: 360 } }}>
                        {t(
                            'The traffic light rates each characteristic of the lookalike against the building being assessed.',
                        )}
                    </Typography>
                )}
            </Stack>

            {referenceStale && !loading && (
                <Alert severity="warning" data-testid="lookalike-stale">
                    {t('Comparison values are based on changed building data — search again.')}
                </Alert>
            )}

            {errorCode && (
                <Alert severity="error" data-testid="lookalike-error">
                    {lookalikeErrorMessage(t, errorCode)}
                </Alert>
            )}

            {selectionFailed && (
                <Alert severity="error" data-testid="lookalike-selection-error">
                    {t('The measure package could not be compared. Please try again.')}
                </Alert>
            )}

            {result?.status === 'NO_MATCH' && (
                <Alert severity="info" data-testid="lookalike-no-match">
                    {result.noMatchReason ?? t('No comparable building found.')}
                </Alert>
            )}

            {result?.status === 'OK' && (
                <Gebaeudevergleich
                    t={t}
                    result={result}
                    subjectName={subjectName}
                    subjectSubtitle={subjectSubtitle}
                    investmentCostPerSqmRange={investmentCostPerSqmRange}
                    investmentCostDerivedTotalRange={investmentCostDerivedTotalRange}
                    onSelectPackage={selectPackage}
                    selecting={selecting}
                />
            )}
        </Stack>
    );
}

const cellSx = {
    px: 2,
    py: 1.25,
    borderBottom: 1,
    borderColor: 'divider',
    verticalAlign: 'middle',
    fontSize: 14,
    textAlign: 'left',
} as const;

/** The subject column: tinted and accented on its left edge across every row. */
const subjectSx = {
    bgcolor: (theme: Theme) => alpha(theme.palette.success.main, 0.08),
    borderLeft: '2px solid',
    borderLeftColor: 'success.main',
} as const;

const overlineSx = {
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'text.secondary',
} as const;

/** The ✓ / ● / ✕ traffic light, with the verdict's reason as tooltip. */
function VerdictBadge({
    t,
    verdict,
    reason,
    testId,
}: {
    t: TFunction;
    verdict: MerkmalVerdict;
    reason?: string | null;
    testId?: string;
}) {
    const { labelKey, color } = VERDICT_BADGE[verdict];
    const icon =
        verdict === 'TRIFFT_ZU' ? (
            <Check sx={{ fontSize: 14 }} />
        ) : verdict === 'ANNAEHERND' ? (
            <FiberManualRecord sx={{ fontSize: 12 }} />
        ) : (
            <Close sx={{ fontSize: 14 }} />
        );
    return (
        <Tooltip title={reason ? `${t(labelKey)} — ${reason}` : t(labelKey)}>
            <Box
                component="span"
                role="img"
                aria-label={t(labelKey)}
                data-verdict={verdict}
                data-testid={testId}
                sx={{
                    flexShrink: 0,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: `${color}.main`,
                    bgcolor: (theme) => alpha(theme.palette[color].main, 0.14),
                }}
            >
                {icon}
            </Box>
        </Tooltip>
    );
}

/** A candidate cell: the value left, its traffic light (or a muted dash) right. */
function CandidateCell({ children, badge, testId }: { children: ReactNode; badge: ReactNode; testId?: string }) {
    return (
        <Box component="td" sx={cellSx} data-testid={testId}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Box component="span" sx={{ minWidth: 0 }}>
                    {children}
                </Box>
                {badge}
            </Box>
        </Box>
    );
}

const NO_BADGE = (
    <Box component="span" sx={{ width: 24, textAlign: 'center', color: 'text.disabled' }}>
        –
    </Box>
);

/** A full-width band header ("Gebäudedaten", "Prognose — Systemwerte"), with an optional muted note. */
function SectionRow({ title, note, colSpan }: { title: string; note?: string; colSpan: number }) {
    return (
        <Box component="tr">
            <Box component="td" colSpan={colSpan} sx={{ ...cellSx, bgcolor: 'action.hover', py: 1.5 }}>
                <Box component="span" sx={{ ...overlineSx, fontWeight: 700, color: 'text.primary', mr: 1.5 }}>
                    {title}
                </Box>
                {note && (
                    <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                        {note}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

interface GebaeudevergleichProps {
    t: TFunction;
    result: LookalikeComparisonDto;
    subjectName: string;
    subjectSubtitle?: string | null;
    investmentCostPerSqmRange?: RangeDto | null;
    investmentCostDerivedTotalRange?: RangeDto | null;
    /** The Maßnahmenpaket picker's change handler; absent = no picker (no job to select on). */
    onSelectPackage?: (docId: string, packageId: string) => void;
    selecting?: boolean;
}

/** The comparison itself — subject column + the ranked lookalikes, traffic lights, Prognose band, €/m². */
function Gebaeudevergleich({
    t,
    result,
    subjectName,
    subjectSubtitle,
    investmentCostPerSqmRange,
    investmentCostDerivedTotalRange,
    onSelectPackage,
    selecting = false,
}: GebaeudevergleichProps) {
    const subjectByMerkmal = new Map(result.subject.map((row) => [row.merkmal, row.value]));
    const candidates = [...result.candidates].sort((a, b) => a.rank - b.rank);
    const colSpan = 2 + candidates.length;

    function candidateValue(candidate: LookalikeCandidateDto, merkmal: MerkmalKey): CandidateMerkmalDto | undefined {
        return candidate.merkmale.find((row) => row.merkmal === merkmal);
    }

    // Investitionskosten/m²: the effective range from `/calculate` (advisor bounds over the stamped
    // aggregation), the search's own aggregation until `/calculate` caught up. Every total is
    // server-side: the subject's derived by `/calculate`, each lookalike's card-joined.
    const subjectPerSqm = investmentCostPerSqmRange ?? result.investmentCostPerSquareMetreRange;
    const subjectTotal = investmentCostDerivedTotalRange ?? null;

    const labelCellSx = { ...cellSx, color: 'text.secondary' } as const;
    const monoSx = { fontFamily: MONO, fontWeight: 600 } as const;

    return (
        <Stack spacing={2}>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
                <Box
                    component="table"
                    sx={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', tableLayout: 'fixed' }}
                    data-testid="lookalike-merkmale"
                >
                    <Box component="colgroup">
                        <Box component="col" sx={{ width: '28%' }} />
                        <Box component="col" sx={{ width: `${72 / (1 + candidates.length)}%` }} />
                        {candidates.map((candidate) => (
                            <Box component="col" key={candidate.docId} />
                        ))}
                    </Box>
                    <Box component="thead">
                        <Box component="tr">
                            <Box component="th" sx={{ ...cellSx, ...overlineSx, verticalAlign: 'bottom', py: 2 }}>
                                {t('Characteristic')}
                            </Box>
                            <Box
                                component="th"
                                sx={{ ...cellSx, ...subjectSx, verticalAlign: 'top', py: 2 }}
                                data-testid="lookalike-subject-head"
                            >
                                <Box sx={{ ...overlineSx, color: 'success.main', mb: 0.75 }}>
                                    {t('Building to assess')}
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                    {subjectName}
                                </Typography>
                                {subjectSubtitle && (
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {subjectSubtitle}
                                    </Typography>
                                )}
                            </Box>
                            {candidates.map((candidate) => {
                                const packages = candidate.packages ?? [];
                                const pickable = onSelectPackage !== undefined && packages.length > 1;
                                const subtitle = [
                                    candidate.municipality,
                                    !pickable &&
                                        candidate.packageName &&
                                        t('Measure package: {{name}}', { name: candidate.packageName }),
                                ]
                                    .filter(Boolean)
                                    .join(' · ');
                                const name = candidate.propertyName ?? candidate.label;
                                return (
                                    <Box
                                        component="th"
                                        key={candidate.docId}
                                        sx={{ ...cellSx, verticalAlign: 'top', py: 2 }}
                                        data-testid={`lookalike-candidate-head-${candidate.rank}`}
                                    >
                                        <Box sx={{ ...overlineSx, mb: 0.75 }}>
                                            {t('Lookalike {{rank}}', { rank: candidate.rank })}
                                        </Box>
                                        <Typography variant="body2" sx={{ fontWeight: 700 }} title={candidate.label}>
                                            {candidate.reportUrl ? (
                                                <Link
                                                    href={candidate.reportUrl}
                                                    target="_blank"
                                                    rel="noopener"
                                                    color="inherit"
                                                    underline="hover"
                                                >
                                                    {name}
                                                </Link>
                                            ) : (
                                                name
                                            )}
                                        </Typography>
                                        {subtitle && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                {subtitle}
                                            </Typography>
                                        )}
                                        {pickable && (
                                            <TextField
                                                select
                                                size="small"
                                                fullWidth
                                                label={t('Measure package')}
                                                value={candidate.packageId ?? ''}
                                                disabled={selecting}
                                                onChange={(event) =>
                                                    onSelectPackage(candidate.docId, event.target.value)
                                                }
                                                sx={{ mt: 1 }}
                                                slotProps={{
                                                    htmlInput: {
                                                        'data-testid': `lookalike-package-${candidate.rank}`,
                                                    },
                                                }}
                                            >
                                                {packages.map((pkg) => (
                                                    <MenuItem key={pkg.packageId} value={pkg.packageId}>
                                                        {pkg.packageId === candidate.suggestedPackageId
                                                            ? t('{{name}} (suggested)', {
                                                                  name: pkg.name ?? pkg.packageId,
                                                              })
                                                            : (pkg.name ?? pkg.packageId)}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    <Box component="tbody">
                        {/* Band 1 — the 12 Merkmale: subject first, then each lookalike with its traffic light. */}
                        <SectionRow title={t('Building data')} colSpan={colSpan} />
                        {MERKMALE.map(({ key, labelKey, format }) => {
                            const subjectValue = subjectByMerkmal.get(key);
                            return (
                                <Box component="tr" key={key} data-testid={`lookalike-row-${key}`}>
                                    <Box component="td" sx={labelCellSx}>
                                        {t(labelKey)}
                                    </Box>
                                    <Box
                                        component="td"
                                        sx={{
                                            ...cellSx,
                                            ...subjectSx,
                                            ...(typeof subjectValue === 'number' ? monoSx : {}),
                                        }}
                                    >
                                        {formatMerkmalValue(t, subjectValue, format)}
                                    </Box>
                                    {candidates.map((candidate) => {
                                        const row = candidateValue(candidate, key);
                                        return (
                                            <CandidateCell
                                                key={candidate.docId}
                                                testId={`lookalike-cell-${key}-${candidate.rank}`}
                                                badge={
                                                    row?.verdict ? (
                                                        <VerdictBadge
                                                            t={t}
                                                            verdict={row.verdict}
                                                            reason={row.reason}
                                                            testId={`lookalike-verdict-${key}-${candidate.rank}`}
                                                        />
                                                    ) : (
                                                        NO_BADGE
                                                    )
                                                }
                                            >
                                                <Box
                                                    component="span"
                                                    sx={typeof row?.value === 'number' ? monoSx : undefined}
                                                >
                                                    {formatMerkmalValue(t, row?.value, format)}
                                                </Box>
                                            </CandidateCell>
                                        );
                                    })}
                                </Box>
                            );
                        })}

                        {/* Band 2 — Prognose: frozen system ranges vs each lookalike's actual, flagged. */}
                        <SectionRow
                            title={t('Forecast — system values')}
                            note={t(
                                'Range from the heuristic against the actual values of the energy consultation. Adjustments below do not change these rows.',
                            )}
                            colSpan={colSpan}
                        />
                        {PROGNOSE_METRICS.map(({ key, labelKey, unit, digits }) => {
                            const band = result.prognoseBand.find((entry) => entry.metric === key);
                            return (
                                <Box component="tr" key={key} data-testid={`lookalike-prognose-${key}`}>
                                    <Box component="td" sx={labelCellSx}>
                                        {t(labelKey)}
                                    </Box>
                                    <Box component="td" sx={{ ...cellSx, ...subjectSx, ...monoSx }}>
                                        {formatRange(band?.systemRange, unit, digits)}
                                    </Box>
                                    {candidates.map((candidate) => {
                                        const outcome = band?.candidates.find(
                                            (entry) => entry.docId === candidate.docId,
                                        );
                                        return (
                                            <CandidateCell
                                                key={candidate.docId}
                                                badge={
                                                    outcome?.status ? (
                                                        <VerdictBadge
                                                            t={t}
                                                            verdict={RANGE_STATUS_VERDICT[outcome.status]}
                                                            testId={`lookalike-flag-${key}-${candidate.rank}`}
                                                        />
                                                    ) : (
                                                        NO_BADGE
                                                    )
                                                }
                                            >
                                                <Box component="span" sx={monoSx}>
                                                    {formatNumber(outcome?.actual, unit, digits)}
                                                </Box>
                                            </CandidateCell>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                        {/* Investitionskosten/m² — flagged client-side against the subject range. */}
                        <Box component="tr" data-testid="lookalike-invest-per-sqm">
                            <Box component="td" sx={labelCellSx}>
                                {t('Investment costs per m²')}
                            </Box>
                            <Box
                                component="td"
                                sx={{ ...cellSx, ...subjectSx, ...monoSx }}
                                data-testid="lookalike-invest-range"
                            >
                                {formatRange(subjectPerSqm, '€/m²', 0)}
                            </Box>
                            {candidates.map((candidate) => {
                                const status = rangeStatus(
                                    subjectPerSqm,
                                    candidate.investmentCostPerSquareMetre,
                                    result.nearRelativeTolerance,
                                );
                                return (
                                    <CandidateCell
                                        key={candidate.docId}
                                        badge={
                                            status ? (
                                                <VerdictBadge
                                                    t={t}
                                                    verdict={RANGE_STATUS_VERDICT[status]}
                                                    testId={`lookalike-flag-INVEST_PER_SQM-${candidate.rank}`}
                                                />
                                            ) : (
                                                NO_BADGE
                                            )
                                        }
                                    >
                                        <Box component="span" sx={monoSx}>
                                            {formatNumber(candidate.investmentCostPerSquareMetre, '€/m²', 0)}
                                        </Box>
                                    </CandidateCell>
                                );
                            })}
                        </Box>
                        {/* Investitionskosten gesamt — derived (€/m² × NGF), shown muted and unflagged. */}
                        <Box component="tr" data-testid="lookalike-invest-total">
                            <Box component="td" sx={labelCellSx}>
                                {t('Total investment costs')}
                                <Box component="span" sx={{ ...overlineSx, fontSize: 10, ml: 1 }}>
                                    {t('derived')}
                                </Box>
                            </Box>
                            <Box
                                component="td"
                                sx={{ ...cellSx, ...subjectSx, fontFamily: MONO, color: 'text.secondary' }}
                                data-testid="lookalike-invest-total-subject"
                            >
                                {formatRange(subjectTotal, '€', 0)}
                            </Box>
                            {candidates.map((candidate) => (
                                <CandidateCell key={candidate.docId} badge={NO_BADGE}>
                                    <Box component="span" sx={{ fontFamily: MONO, color: 'text.secondary' }}>
                                        {formatNumber(roundedTotal(candidate.investmentCostTotal), '€', 0)}
                                    </Box>
                                </CandidateCell>
                            ))}
                        </Box>
                    </Box>
                </Box>

                {/* Legend */}
                <Stack
                    direction="row"
                    spacing={3}
                    useFlexGap
                    sx={{ px: 2, py: 1.25, bgcolor: 'action.hover', flexWrap: 'wrap', alignItems: 'center' }}
                    data-testid="lookalike-legend"
                >
                    {(Object.keys(VERDICT_BADGE) as MerkmalVerdict[]).map((verdict) => (
                        <Stack key={verdict} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                            <VerdictBadge t={t} verdict={verdict} />
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {t(VERDICT_BADGE[verdict].labelKey)}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>

            {/* Caveats and notes per lookalike — plain text (no markdown/links). */}
            {candidates.some((candidate) => candidate.caveats.length + candidate.notes.length > 0) && (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    {candidates.map((candidate) => (
                        <Box key={candidate.docId} sx={{ flex: 1 }} data-testid={`lookalike-notes-${candidate.rank}`}>
                            <Box sx={{ ...overlineSx, mb: 0.5 }}>
                                {t('Lookalike {{rank}}', { rank: candidate.rank })}
                            </Box>
                            {[...candidate.caveats, ...candidate.notes].map((line, index) => (
                                <Typography
                                    key={index}
                                    variant="caption"
                                    sx={{ display: 'block', color: 'text.secondary' }}
                                >
                                    • {line}
                                </Typography>
                            ))}
                        </Box>
                    ))}
                </Stack>
            )}
        </Stack>
    );
}
